import { useState, useEffect } from "react";
import { LucideSparkles, LucideLoader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Modal } from "@/components/modals/Modal";
import { BACKEND_URL } from "@/constants/api";
import { useAuth } from "@clerk/clerk-react";
import { buildUserPrompt } from "@/reports/shared_utils/buildUserPrompt";

/**
 * Modal that lets teachers review and adjust AI generation parameters before
 * submitting. Shows an editable system prompt, read-only report info, the
 * existing comment, and a free-text instructions field.
 *
 * Props:
 *   open                - controlled open state
 *   onClose             - called when the modal should close
 *   defaultSystemPrompt - initial value for the system prompt textarea
 *   studentReportInfo   - object of key/value pairs displayed read-only
 *   currentComment      - current textarea value; shown read-only as context
 *   reportId            - passed to the backend
 *   onCommentGenerated  - called with the generated comment string on success
 */

const SYSTEM_PROMPT_LIMIT = 3000;
const INSTRUCTIONS_LIMIT = 1000;

function autoResizeTextarea(el) {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = el.scrollHeight + "px";
}

function formatKey(key) {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

export function AICommentModal({
  open,
  onClose,
  defaultSystemPrompt,
  studentReportInfo,
  currentComment,
  reportId,
  onCommentGenerated,
}) {
  const { getToken } = useAuth();

  const [systemPrompt, setSystemPrompt] = useState(defaultSystemPrompt || "");
  const [additionalInstructions, setAdditionalInstructions] = useState("");
  const [showSystemPrompt, setShowSystemPrompt] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);

  useEffect(() => {
    if (open) {
      setSystemPrompt(defaultSystemPrompt || "");
      setAdditionalInstructions("");
      setShowSystemPrompt(false);
      setError(null);
      setErrorDetails(null);
    }
  }, [open, defaultSystemPrompt]);

  const handleGenerate = async () => {
    setError(null);
    setErrorDetails(null);

    const token = await getToken();
    if (!token) {
      setError("Sign in to use AI comment generation.");
      return;
    }

    setIsGenerating(true);
    try {
      const userPrompt = buildUserPrompt(currentComment, additionalInstructions);
      const res = await fetch(`${BACKEND_URL}api/report-comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          reportId: reportId || "free-trial",
          systemPrompt,
          userPrompt,
          // Send field names only — backend fetches values from DB to prevent client-supplied data reaching the AI prompt.
          reportInfoFields: Object.keys(studentReportInfo || {}),
        }),
      });

      if (res.status === 429) {
        const body = await res.json().catch(() => ({}));
        const retryMins = Math.ceil((body.retryAfterSeconds ?? 900) / 60);
        setError("You've sent too many requests.");
        setErrorDetails(
          `Please wait approximately ${retryMins} minute${retryMins !== 1 ? "s" : ""} before trying again.`
        );
        return;
      }
      if (res.status === 403) {
        const body = await res.json();
        setError(body.message || "AI generation quota exceeded. Please upgrade your plan.");
        return;
      }
      if (!res.ok) {
        setError("Failed to generate AI comment. Please try again.");
        return;
      }

      const { comment } = await res.json();
      onCommentGenerated(comment);
    } catch {
      setError("Failed to generate AI comment. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const infoEntries = Object.entries(studentReportInfo || {}).filter(
    ([, v]) => v !== undefined && v !== null && v !== ""
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Generate with AI"
      icon={<LucideSparkles size={16} className="text-primary" />}
      maxWidth="max-w-2xl"
      maxHeight="max-h-[90vh]"
    >
      <div className="px-6 py-5 overflow-y-auto flex-1 space-y-5">

        {/* System Prompt - collapsible */}
        <div className="space-y-1.5 border-t border-border pt-4">
          <div className="flex items-center justify-between">
            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              System Prompt
            </label>
            <button
              type="button"
              onClick={() => setShowSystemPrompt((v) => !v)}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              {showSystemPrompt ? "Hide" : "Edit"}
            </button>
          </div>
          {!showSystemPrompt && (
            <p className="text-xs text-muted-foreground italic">Using default AI behavior.</p>
          )}
          {showSystemPrompt && (
            <>
              <Textarea
                value={systemPrompt}
                onChange={(e) => {
                  setSystemPrompt(e.target.value.slice(0, SYSTEM_PROMPT_LIMIT));
                  autoResizeTextarea(e.target);
                }}
                ref={(el) => el && autoResizeTextarea(el)}
                className="resize-none text-sm font-mono overflow-hidden"
              />
              <p className={`text-xs text-right ${systemPrompt.length > SYSTEM_PROMPT_LIMIT * 0.9 ? "text-destructive" : "text-muted-foreground"}`}>
                {systemPrompt.length}/{SYSTEM_PROMPT_LIMIT}
              </p>
              <p className="text-xs text-muted-foreground">Advanced: controls tone and style.</p>
            </>
          )}
        </div>

        {/* Report Info */}
        {infoEntries.length > 0 && (
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground block">
              Report Info
            </label>
            <div className="rounded-lg border border-border bg-muted/50 p-3">
              <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                {infoEntries.map(([key, value]) => (
                  <div key={key} className="flex gap-1.5 min-w-0">
                    <dt className="text-[11px] text-muted-foreground shrink-0">{formatKey(key)}:</dt>
                    <dd className="text-[11px] text-foreground font-medium truncate">{String(value)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        )}

        {/* Existing Comment */}
        <div className="space-y-1.5">
          <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground block">
            Existing Comment
          </label>
          <div className="rounded-lg border border-border bg-muted/50 p-3 text-sm text-muted-foreground min-h-[60px] whitespace-pre-wrap">
            {currentComment?.trim() ? (
              <span className="text-foreground">{currentComment}</span>
            ) : (
              <span className="italic text-xs">No existing comment - AI will generate from scratch.</span>
            )}
          </div>
        </div>

        {/* Additional Instructions */}
        <div className="space-y-1.5">
          <label className="text-[11px] font-semibold text-foreground block">
            What should the AI focus on?
          </label>
          <Textarea
            value={additionalInstructions}
            onChange={(e) => {
              setAdditionalInstructions(e.target.value.slice(0, INSTRUCTIONS_LIMIT));
              autoResizeTextarea(e.target);
            }}
            ref={(el) => el && autoResizeTextarea(el)}
            placeholder="e.g. Focus on reading progress. Keep it to 2 sentences. Mention strengths first."
            className="resize-none text-sm overflow-hidden"
          />
          <p className={`text-xs text-right ${additionalInstructions.length > INSTRUCTIONS_LIMIT * 0.9 ? "text-destructive" : "text-muted-foreground"}`}>
            {additionalInstructions.length}/{INSTRUCTIONS_LIMIT}
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3.5 py-3">
            <p className="text-sm font-medium text-destructive">{error}</p>
            {errorDetails && <p className="mt-1 text-xs text-destructive/80">{errorDetails}</p>}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-2 border-t border-border">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={isGenerating}>
            Cancel
          </Button>
          <Button type="button" size="sm" onClick={handleGenerate} disabled={isGenerating}>
            {isGenerating
              ? <LucideLoader2 size={14} className="mr-1.5 animate-spin" />
              : <LucideSparkles size={14} className="mr-1.5" />
            }
            Generate
          </Button>
        </div>

      </div>
    </Modal>
  );
}
